/**
 * Módulo de Prospecção Ativa de Clientes
 * ======================================
 * Interface para gerenciar prospecção automatizada via WhatsApp
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Users, Target, MessageSquare, CheckCircle, XCircle, Send, Settings, BarChart3, RefreshCw, Zap, MessageCircle, Gift } from 'lucide-react';
import { getBackendUrl } from './utils/api';
import EngagementLojistas from './components/EngagementLojistas';
import { Tab } from './components/prospection/shared';
import { DashboardTab, WhatsAppTab, SegmentsTab, LeadsTab, CampaignsTab, SchedulerTab, ConversationsTab, SettingsTab, SegmentModal, CampaignModal } from './components/prospection';

const API_URL = getBackendUrl() || '';

export default function ProspectionModule({ token }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Estados dos dados
  const [dashboard, setDashboard] = useState(null);
  const [segments, setSegments] = useState([]);
  const [leads, setLeads] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [settings, setSettings] = useState(null);

  // Estados de UI
  const [showSegmentModal, setShowSegmentModal] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [editingSegment, setEditingSegment] = useState(null);
  const [selectedSegmentFilter, setSelectedSegmentFilter] = useState('');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Estados WhatsApp
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [whatsappConnecting, setWhatsappConnecting] = useState(false);
  const [whatsappQrCode, setWhatsappQrCode] = useState(null);
  const [flowSettings, setFlowSettings] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  
  // Estados de Conversas
  const [leadsNeedingAttention, setLeadsNeedingAttention] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [manualResponse, setManualResponse] = useState('');
  const [sendingResponse, setSendingResponse] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  
  // Estados do Scheduler Automático
  const [schedulerConfig, setSchedulerConfig] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [queueStats, setQueueStats] = useState(null);
  const [schedulerHistory, setSchedulerHistory] = useState([]);
  const [savingScheduler, setSavingScheduler] = useState(false);
  
  // Fetch helpers
  const fetchWithAuth = useCallback(async (endpoint, options = {}) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro desconhecido' }));
      throw new Error(error.detail || 'Erro na requisição');
    }
    return response.json();
  }, [token]);

  // Carregar dados
  const loadDashboard = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/prospection/dashboard');
      setDashboard(data);
    } catch (err) {
      console.error('Erro ao carregar dashboard:', err);
    }
  }, [fetchWithAuth]);

  const loadSegments = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/prospection/segments');
      setSegments(data.segments || []);
    } catch (err) {
      console.error('Erro ao carregar segmentos:', err);
    }
  }, [fetchWithAuth]);

  const loadLeads = useCallback(async () => {
    try {
      let endpoint = '/api/prospection/leads?limit=100';
      if (selectedSegmentFilter) endpoint += `&segment_id=${selectedSegmentFilter}`;
      if (selectedStatusFilter) endpoint += `&status=${selectedStatusFilter}`;
      
      const data = await fetchWithAuth(endpoint);
      setLeads(data.leads || []);
    } catch (err) {
      console.error('Erro ao carregar leads:', err);
    }
  }, [fetchWithAuth, selectedSegmentFilter, selectedStatusFilter]);

  const loadCampaigns = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/prospection/campaigns');
      setCampaigns(data.campaigns || []);
    } catch (err) {
      console.error('Erro ao carregar campanhas:', err);
    }
  }, [fetchWithAuth]);

  const loadSettings = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/prospection/settings');
      setSettings(data);
    } catch (err) {
      console.error('Erro ao carregar configurações:', err);
    }
  }, [fetchWithAuth]);

  // Funções WhatsApp
  const loadWhatsappStatus = useCallback(async (forceRefresh = false) => {
    try {
      const url = forceRefresh 
        ? '/api/prospection/whatsapp/status?refresh=true' 
        : '/api/prospection/whatsapp/status';
      const data = await fetchWithAuth(url);
      setWhatsappStatus(data);
    } catch (err) {
      console.error('Erro ao carregar status WhatsApp:', err);
      setWhatsappStatus({ connected: false, status: 'error', message: err.message });
    }
  }, [fetchWithAuth]);

  const loadFlowSettings = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/prospection/whatsapp/flow-settings');
      setFlowSettings(data);
    } catch (err) {
      console.error('Erro ao carregar flow settings:', err);
    }
  }, [fetchWithAuth]);
  
  // Funções de Conversas e Analytics
  const loadLeadsNeedingAttention = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/prospection/leads/needs-attention');
      setLeadsNeedingAttention(data.leads || []);
    } catch (err) {
      console.error('Erro ao carregar leads que precisam de atenção:', err);
    }
  }, [fetchWithAuth]);
  
  const loadAnalytics = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/prospection/analytics?days=30');
      setAnalytics(data);
    } catch (err) {
      console.error('Erro ao carregar analytics:', err);
    }
  }, [fetchWithAuth]);
  
  const loadLeadConversation = useCallback(async (leadId) => {
    try {
      const data = await fetchWithAuth(`/api/prospection/leads/${leadId}/conversation`);
      setConversationHistory(data.conversation_history || []);
      setSelectedLead(data);
    } catch (err) {
      console.error('Erro ao carregar conversa:', err);
      showMessage('Erro ao carregar conversa', true);
    }
  }, [fetchWithAuth]);
  
  const handleSendManualResponse = async () => {
    if (!selectedLead || !manualResponse.trim()) return;
    
    setSendingResponse(true);
    try {
      await fetchWithAuth(`/api/prospection/leads/${selectedLead.lead_id}/manual-response`, {
        method: 'POST',
        body: JSON.stringify({ message: manualResponse })
      });
      
      showMessage('Resposta enviada com sucesso!');
      setManualResponse('');
      
      // Recarregar conversa
      await loadLeadConversation(selectedLead.lead_id);
      await loadLeadsNeedingAttention();
    } catch (err) {
      showMessage(err.message, true);
    } finally {
      setSendingResponse(false);
    }
  };
  
  const handleBlacklistLead = async (leadId) => {
    if (!window.confirm('Tem certeza que deseja adicionar este lead à blacklist?')) return;
    
    try {
      await fetchWithAuth(`/api/prospection/leads/${leadId}/blacklist`, {
        method: 'POST'
      });
      showMessage('Lead adicionado à blacklist');
      await loadLeadsNeedingAttention();
      setSelectedLead(null);
    } catch (err) {
      showMessage(err.message, true);
    }
  };
  
  const handleStartCampaign = async (campaignId) => {
    try {
      await fetchWithAuth(`/api/prospection/campaigns/${campaignId}/start`, {
        method: 'POST'
      });
      showMessage('Campanha iniciada! Os envios começarão em breve.');
      loadCampaigns();
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  // ==================== SCHEDULER AUTOMÁTICO ====================
  
  const loadSchedulerConfig = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/prospection/scheduler/config');
      setSchedulerConfig(data);
    } catch (err) {
      console.error('Erro ao carregar config do scheduler:', err);
    }
  }, [fetchWithAuth]);
  
  const loadSchedulerStatus = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/prospection/scheduler/status');
      setSchedulerStatus(data);
    } catch (err) {
      console.error('Erro ao carregar status do scheduler:', err);
    }
  }, [fetchWithAuth]);
  
  const loadQueueStats = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/prospection/queue/stats');
      setQueueStats(data);
    } catch (err) {
      console.error('Erro ao carregar stats da fila:', err);
    }
  }, [fetchWithAuth]);
  
  const loadSchedulerHistory = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/prospection/scheduler/history?days=7');
      setSchedulerHistory(data.history || []);
    } catch (err) {
      console.error('Erro ao carregar histórico do scheduler:', err);
    }
  }, [fetchWithAuth]);
  
  const handleStartScheduler = async () => {
    try {
      await fetchWithAuth('/api/prospection/scheduler/start', { method: 'POST' });
      showMessage('🚀 Scheduler Automático INICIADO! Os envios começarão conforme configurado.');
      await loadSchedulerStatus();
      await loadSchedulerConfig();
    } catch (err) {
      showMessage(err.message, true);
    }
  };
  
  const handleStopScheduler = async () => {
    if (!window.confirm('Tem certeza que deseja PARAR o scheduler automático?')) return;
    
    try {
      await fetchWithAuth('/api/prospection/scheduler/stop', { method: 'POST' });
      showMessage('⏹️ Scheduler Automático PARADO.');
      await loadSchedulerStatus();
      await loadSchedulerConfig();
    } catch (err) {
      showMessage(err.message, true);
    }
  };
  
  const handleSaveSchedulerConfig = async () => {
    setSavingScheduler(true);
    try {
      await fetchWithAuth('/api/prospection/scheduler/config', {
        method: 'PUT',
        body: JSON.stringify(schedulerConfig)
      });
      showMessage('Configurações salvas!');
    } catch (err) {
      showMessage(err.message, true);
    } finally {
      setSavingScheduler(false);
    }
  };
  
  const handleDeduplicateQueue = async () => {
    if (!window.confirm('Isso irá remover leads com telefones duplicados. Continuar?')) return;
    
    try {
      const result = await fetchWithAuth('/api/prospection/queue/deduplicate', { method: 'POST' });
      showMessage(`🔄 ${result.removed} duplicatas removidas!`);
      await loadQueueStats();
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  const handleConnectWhatsapp = async () => {
    setWhatsappConnecting(true);
    setWhatsappQrCode(null);
    try {
      const data = await fetchWithAuth('/api/prospection/whatsapp/connect', {
        method: 'POST',
        body: JSON.stringify({ instance_name: 'prospection_master' })
      });
      
      if (data.already_connected) {
        showMessage('WhatsApp já está conectado!');
        setWhatsappConnecting(false);
        await loadWhatsappStatus();
        return;
      }
      
      if (data.qr_code) {
        setWhatsappQrCode(data.qr_code);
        showMessage('QR Code gerado! Escaneie com o WhatsApp.');
        
        // Polling para verificar conexão (lê do banco, atualizado pelo webhook)
        let attempts = 0;
        const checkConnection = setInterval(async () => {
          attempts++;
          try {
            const statusData = await fetchWithAuth('/api/prospection/whatsapp/status');
            setWhatsappStatus(statusData);
            
            // Se tem novo QR code, atualizar
            if (statusData?.qr_code && statusData?.status === 'qrcode') {
              setWhatsappQrCode(statusData.qr_code);
            }
            
            if (statusData?.connected || attempts >= 90) {
              clearInterval(checkConnection);
              setWhatsappConnecting(false);
              setWhatsappQrCode(null);
              
              if (statusData?.connected) {
                showMessage('WhatsApp conectado com sucesso!');
              } else {
                showMessage('Tempo esgotado. Tente reconectar.', true);
              }
            }
          } catch (pollErr) {
            console.error('Polling error:', pollErr);
          }
        }, 3000);
      } else {
        showMessage('QR Code não disponível. Tente novamente.', true);
        setWhatsappConnecting(false);
      }
    } catch (err) {
      showMessage(err.message, true);
      setWhatsappConnecting(false);
    }
  };

  const handleDisconnectWhatsapp = async () => {
    if (!window.confirm('Tem certeza que deseja desconectar o WhatsApp?')) return;
    
    try {
      await fetchWithAuth('/api/prospection/whatsapp/disconnect', { method: 'DELETE' });
      setWhatsappStatus({ connected: false, status: 'disconnected' });
      showMessage('WhatsApp desconectado.');
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  const handleSendTestMessage = async () => {
    if (!testPhone) {
      showMessage('Digite um número de telefone', true);
      return;
    }
    
    try {
      await fetchWithAuth('/api/prospection/whatsapp/send-test', {
        method: 'POST',
        body: JSON.stringify({ phone: testPhone })
      });
      showMessage('Mensagem de teste enviada!');
      setTestPhone('');
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  const handleSaveFlowSettings = async (newSettings) => {
    try {
      await fetchWithAuth('/api/prospection/whatsapp/flow-settings', {
        method: 'PUT',
        body: JSON.stringify(newSettings)
      });
      setFlowSettings(newSettings);
      showMessage('Configurações de fluxo salvas!');
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  // Carregar dados iniciais
  useEffect(() => {
    loadDashboard();
    loadSegments();
    loadSettings();
    loadWhatsappStatus();
    loadFlowSettings();
    loadAnalytics();
  }, [loadDashboard, loadSegments, loadSettings, loadWhatsappStatus, loadFlowSettings, loadAnalytics]);

  // Recarregar dados do WhatsApp quando aba muda
  useEffect(() => {
    if (activeTab === 'whatsapp') {
      loadWhatsappStatus();
      loadFlowSettings();
    }
  }, [activeTab, loadWhatsappStatus, loadFlowSettings]);

  // Recarregar leads quando filtros mudam
  useEffect(() => {
    if (activeTab === 'leads') {
      loadLeads();
    }
  }, [activeTab, loadLeads]);

  // Recarregar campanhas quando aba muda
  useEffect(() => {
    if (activeTab === 'campaigns') {
      loadCampaigns();
    }
  }, [activeTab, loadCampaigns]);
  
  // Carregar conversas quando aba muda
  useEffect(() => {
    if (activeTab === 'conversations') {
      loadLeadsNeedingAttention();
    }
  }, [activeTab, loadLeadsNeedingAttention]);
  
  // Carregar dados do scheduler quando aba muda
  useEffect(() => {
    if (activeTab === 'scheduler') {
      loadSchedulerConfig();
      loadSchedulerStatus();
      loadQueueStats();
      loadSchedulerHistory();
    }
  }, [activeTab, loadSchedulerConfig, loadSchedulerStatus, loadQueueStats, loadSchedulerHistory]);
  
  // Auto-refresh do status do scheduler a cada 30s
  useEffect(() => {
    if (activeTab === 'scheduler' && schedulerConfig?.enabled) {
      const interval = setInterval(() => {
        loadSchedulerStatus();
        loadQueueStats();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [activeTab, schedulerConfig?.enabled, loadSchedulerStatus, loadQueueStats]);

  // Handlers
  const showMessage = (msg, isError = false) => {
    if (isError) {
      setError(msg);
      setTimeout(() => setError(null), 5000);
    } else {
      setSuccess(msg);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleSaveSegment = async (segmentData) => {
    setLoading(true);
    try {
      if (editingSegment?.id) {
        await fetchWithAuth(`/api/prospection/segments/${editingSegment.id}`, {
          method: 'PUT',
          body: JSON.stringify(segmentData)
        });
        showMessage('Segmento atualizado com sucesso!');
      } else {
        await fetchWithAuth('/api/prospection/segments', {
          method: 'POST',
          body: JSON.stringify(segmentData)
        });
        showMessage('Segmento criado com sucesso!');
      }
      loadSegments();
      setShowSegmentModal(false);
      setEditingSegment(null);
    } catch (err) {
      showMessage(err.message, true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSegment = async (segmentId) => {
    if (!window.confirm('Tem certeza que deseja excluir este segmento?')) return;
    
    setLoading(true);
    try {
      await fetchWithAuth(`/api/prospection/segments/${segmentId}`, { method: 'DELETE' });
      showMessage('Segmento excluído!');
      loadSegments();
    } catch (err) {
      showMessage(err.message, true);
    } finally {
      setLoading(false);
    }
  };

  const handleImportCSV = async (file, segmentId) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('segment_id', segmentId);

      const response = await fetch(`${API_URL}/api/prospection/leads/import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail);

      showMessage(`Importação concluída! ${data.imported} leads importados, ${data.duplicates} duplicados.`);
      loadLeads();
      loadDashboard();
    } catch (err) {
      showMessage(err.message, true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Tem certeza que deseja excluir este lead?')) return;
    
    try {
      await fetchWithAuth(`/api/prospection/leads/${leadId}`, { method: 'DELETE' });
      showMessage('Lead excluído!');
      loadLeads();
      loadDashboard();
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  const handleCampaignStatus = async (campaignId, status) => {
    try {
      await fetchWithAuth(`/api/prospection/campaigns/${campaignId}/status?status=${status}`, {
        method: 'PUT'
      });
      showMessage(`Campanha ${status === 'active' ? 'ativada' : status === 'paused' ? 'pausada' : status}!`);
      loadCampaigns();
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  const handleDeleteCampaign = async (campaignId, campaignName) => {
    if (!window.confirm(`Excluir campanha "${campaignName}"? Esta ação não pode ser desfeita.`)) return;
    
    try {
      await fetchWithAuth(`/api/prospection/campaigns/${campaignId}`, { method: 'DELETE' });
      showMessage('Campanha excluída!');
      loadCampaigns();
      loadDashboard();
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  const handleDeleteLeadsBySegment = async (segmentId, segmentName) => {
    const label = segmentName || 'todos os leads sem segmento';
    if (!window.confirm(`Excluir todos os leads de "${label}"? Esta ação não pode ser desfeita.`)) return;
    
    try {
      const params = segmentId ? `?segment_id=${segmentId}` : '?status=new';
      await fetchWithAuth(`/api/prospection/leads${params}`, { method: 'DELETE' });
      showMessage('Leads excluídos!');
      loadLeads();
      loadDashboard();
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  const handleSaveSettings = async (newSettings) => {
    setLoading(true);
    try {
      await fetchWithAuth('/api/prospection/settings', {
        method: 'PUT',
        body: JSON.stringify(newSettings)
      });
      showMessage('Configurações salvas!');
      setSettings(newSettings);
    } catch (err) {
      showMessage(err.message, true);
    } finally {
      setLoading(false);
    }
  };

  // ==================== RENDERIZAÇÃO ====================

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Target className="text-blue-400" />
            Prospecção Ativa
          </h2>
          <p className="text-gray-400 mt-1">Gerencie leads e campanhas de prospecção via WhatsApp</p>
        </div>
        <button
          onClick={() => { loadDashboard(); loadSegments(); loadLeads(); loadCampaigns(); }}
          className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300"
          title="Atualizar dados"
        >
          <RefreshCw size={20} />
        </button>
      </div>

      {/* Mensagens */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 text-red-300 px-4 py-3 rounded-lg flex items-center gap-2">
          <XCircle size={20} />
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-500/20 border border-green-500/50 text-green-300 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle size={20} />
          {success}
        </div>
      )}

      {/* Tabs - Scrollável no mobile */}
      <div className="flex border-b border-gray-700 overflow-x-auto pb-px -mx-4 px-4 sm:mx-0 sm:px-0" style={{ WebkitOverflowScrolling: 'touch' }}>
        <Tab active={activeTab === 'dashboard'} onClick={() => setActiveTab('dashboard')} icon={BarChart3}>
          Dash
        </Tab>
        <Tab active={activeTab === 'whatsapp'} onClick={() => setActiveTab('whatsapp')} icon={MessageSquare}>
          Zap
        </Tab>
        <Tab active={activeTab === 'engagement'} onClick={() => setActiveTab('engagement')} icon={Gift}>
          Lojistas
        </Tab>
        <Tab active={activeTab === 'conversations'} onClick={() => setActiveTab('conversations')} icon={MessageCircle}>
          Chat
          {leadsNeedingAttention.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">
              {leadsNeedingAttention.length}
            </span>
          )}
        </Tab>
        <Tab active={activeTab === 'segments'} onClick={() => setActiveTab('segments')} icon={Target}>
          Seg
        </Tab>
        <Tab active={activeTab === 'leads'} onClick={() => setActiveTab('leads')} icon={Users}>
          Leads
        </Tab>
        <Tab active={activeTab === 'campaigns'} onClick={() => setActiveTab('campaigns')} icon={Send}>
          Camp
        </Tab>
        <Tab active={activeTab === 'scheduler'} onClick={() => setActiveTab('scheduler')} icon={Zap}>
          Auto
          {schedulerConfig?.enabled && (
            <span className="ml-1 w-2 h-2 bg-green-500 rounded-full animate-pulse inline-block" />
          )}
        </Tab>
        <Tab active={activeTab === 'settings'} onClick={() => setActiveTab('settings')} icon={Settings}>
          Cfg
        </Tab>
      </div>

      {/* Conteúdo das Tabs */}
      <div className="min-h-[500px]">
        {/* Dashboard */}
        {activeTab === 'dashboard' && (
          <DashboardTab dashboard={dashboard} />
        )}

        {/* WhatsApp */}
        {activeTab === 'whatsapp' && (
          <WhatsAppTab
            status={whatsappStatus}
            qrCode={whatsappQrCode}
            connecting={whatsappConnecting}
            flowSettings={flowSettings}
            testPhone={testPhone}
            onConnect={handleConnectWhatsapp}
            onDisconnect={handleDisconnectWhatsapp}
            onTestPhoneChange={setTestPhone}
            onSendTest={handleSendTestMessage}
            onSaveFlowSettings={handleSaveFlowSettings}
            onRefreshStatus={() => loadWhatsappStatus(true)}
          />
        )}

        {/* Engagement de Lojistas (Onboarding) */}
        {activeTab === 'engagement' && (
          <EngagementLojistas token={token} apiUrl={API_URL} />
        )}

        {/* Segmentos */}
        {activeTab === 'segments' && (          <SegmentsTab
            segments={segments}
            onAdd={() => { setEditingSegment(null); setShowSegmentModal(true); }}
            onEdit={(seg) => { setEditingSegment(seg); setShowSegmentModal(true); }}
            onDelete={handleDeleteSegment}
          />
        )}

        {/* Leads */}
        {activeTab === 'leads' && (
          <LeadsTab
            leads={leads}
            segments={segments}
            selectedSegment={selectedSegmentFilter}
            selectedStatus={selectedStatusFilter}
            searchTerm={searchTerm}
            onSegmentChange={setSelectedSegmentFilter}
            onStatusChange={setSelectedStatusFilter}
            onSearchChange={setSearchTerm}
            onImport={handleImportCSV}
            onDelete={handleDeleteLead}
            onDeleteBySegment={handleDeleteLeadsBySegment}
            loading={loading}
            token={token}
          />
        )}
        
        {/* Conversas */}
        {activeTab === 'conversations' && (
          <ConversationsTab
            leadsNeedingAttention={leadsNeedingAttention}
            selectedLead={selectedLead}
            conversationHistory={conversationHistory}
            manualResponse={manualResponse}
            sendingResponse={sendingResponse}
            onSelectLead={loadLeadConversation}
            onManualResponseChange={setManualResponse}
            onSendResponse={handleSendManualResponse}
            onBlacklist={handleBlacklistLead}
            onRefresh={loadLeadsNeedingAttention}
          />
        )}

        {/* Campanhas */}
        {activeTab === 'campaigns' && (
          <CampaignsTab
            campaigns={campaigns}
            segments={segments}
            onAdd={() => setShowCampaignModal(true)}
            onStatusChange={handleCampaignStatus}
            onStartCampaign={handleStartCampaign}
            onDelete={handleDeleteCampaign}
          />
        )}
        
        {/* Piloto Automático (Scheduler) */}
        {activeTab === 'scheduler' && (
          <SchedulerTab
            config={schedulerConfig}
            status={schedulerStatus}
            queueStats={queueStats}
            history={schedulerHistory}
            segments={segments}
            saving={savingScheduler}
            onConfigChange={setSchedulerConfig}
            onSaveConfig={handleSaveSchedulerConfig}
            onStart={handleStartScheduler}
            onStop={handleStopScheduler}
            onDeduplicate={handleDeduplicateQueue}
            onRefresh={() => {
              loadSchedulerStatus();
              loadQueueStats();
            }}
          />
        )}

        {/* Configurações */}
        {activeTab === 'settings' && (
          <SettingsTab
            settings={settings}
            onSave={handleSaveSettings}
            loading={loading}
          />
        )}
      </div>

      {/* Modal de Segmento */}
      {showSegmentModal && (
        <SegmentModal
          segment={editingSegment}
          onSave={handleSaveSegment}
          onClose={() => { setShowSegmentModal(false); setEditingSegment(null); }}
          loading={loading}
        />
      )}

      {/* Modal de Campanha */}
      {showCampaignModal && (
        <CampaignModal
          segments={segments}
          token={token}
          onClose={() => setShowCampaignModal(false)}
          onSuccess={() => { loadCampaigns(); setShowCampaignModal(false); showMessage('Campanha criada!'); }}
        />
      )}
    </div>
  );
}

// ==================== COMPONENTES DAS TABS ====================
